import {Box, Tooltip, Typography} from "@mui/material";
import {Swiper, SwiperSlide} from "swiper/react";
import {Autoplay} from "swiper/modules";
import "swiper/css";
import {c, mono, softShadow} from "../landingTokens";
import {Section} from "./Shell";
import axiansLogo from "../../../assets/images/axians.webp";
import fitrLogo from "../../../assets/images/fitr.png";
import zanMitrevLogo from "../../../assets/images/zan-mitrev.png";
import a1Logo from "../../../assets/images/A1_hr.png";

const CLIENTS = [
    {
        name: "Zan Mitrev Clinic",
        image: zanMitrevLogo,
        href: "https://zmc.mk/",
        maxHeight: 88,
        maxWidth: 116,
        offsetY: 8
    },
    {name: "A1 Hrvatska", image: a1Logo, href: "https://www.a1.hr/", maxHeight: 76, maxWidth: 88},
];

const PARTNERS = [
    {name: "FITR", image: fitrLogo, href: "https://fitr.wearetribeca.io/", maxHeight: 68, maxWidth: 136},
    {name: "Axians", image: axiansLogo, href: "https://www.axians.mk/", maxHeight: 50, maxWidth: 210},
];

// Swiper's loop needs more slides than are on screen: the clients are repeated until there are enough
const MIN_LOOP_SLIDES = 10;
const loopedClients = Array.from(
    {length: Math.ceil(MIN_LOOP_SLIDES / CLIENTS.length) * CLIENTS.length},
    (_, i) => ({...CLIENTS[i % CLIENTS.length], key: `${CLIENTS[i % CLIENTS.length].name}-${i}`})
);

const hairline = "1px solid rgba(124,108,246,0.14)";

const labelSx = {
    fontFamily: mono,
    fontSize: "11.5px",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: c.purpleDeep,
};

// Soft white trust panel the logos sit in
const panelSx = {
    bgcolor: c.white,
    borderRadius: "28px",
    boxShadow: softShadow,
    px: {xs: 2, md: 3},
    py: {xs: 2, md: 1.5},
};

const Logo = ({item, sx}) => (
    <Tooltip title={item.name} arrow>
        <Box
            component={item.href ? "a" : "div"}
            href={item.href}
            target={item.href ? "_blank" : undefined}
            rel={item.href ? "noopener noreferrer" : undefined}
            sx={{
                height: {xs: 96, md: 112},
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                ...sx,
            }}
        >
            <Box
                component="img"
                src={item.image}
                alt={item.name}
                draggable={false}
                sx={{
                    maxWidth: item.maxWidth || "100%",
                    maxHeight: item.maxHeight || 68,
                    objectFit: "contain",
                    filter: "grayscale(1) contrast(0.9)",
                    opacity: 0.62,
                    transform: item.offsetY ? `translateY(${item.offsetY}px)` : "none",
                    transition: "filter .25s ease, opacity .25s ease",
                    "&:hover": {
                        filter: "grayscale(0) contrast(1)",
                        opacity: 1,
                    },
                }}
            />
        </Box>
    </Tooltip>
);

export const Partners = () => (
    <Section id="backed" label="Partners" sx={{textAlign: "center"}}>
        <Typography sx={{...labelSx, mb: 4.25}}>Trusted by</Typography>

        {/* Clients slide by on their own; hover pauses, drag to browse. */}
        <Box className="reveal" sx={{
            ...panelSx,
            overflow: "hidden",
            "& .swiper-slide": {borderLeft: hairline},
        }}>
            <Swiper
                modules={[Autoplay]}
                loop
                grabCursor
                speed={700}
                autoplay={{delay: 2400, disableOnInteraction: false, pauseOnMouseEnter: true}}
                slidesPerView={2}
                breakpoints={{600: {slidesPerView: 3}, 900: {slidesPerView: 4}}}
            >
                {loopedClients.map(client => (
                    <SwiperSlide key={client.key}>
                        <Logo item={client}/>
                    </SwiperSlide>
                ))}
            </Swiper>
        </Box>

        <Typography sx={{...labelSx, mt: 7, mb: 4.25}}>Built and backed with</Typography>

        <Box
            className="reveal-stagger"
            sx={{
                ...panelSx,
                display: "grid",
                gridTemplateColumns: `repeat(${PARTNERS.length}, minmax(0, 1fr))`,
                maxWidth: 640,
                mx: "auto",
            }}
        >
            {PARTNERS.map((partner, i) => (
                <Logo key={partner.name} item={partner} sx={{borderLeft: i === 0 ? "none" : hairline}}/>
            ))}
        </Box>
    </Section>
);
